import { FormManagementAnalytics } from '@/components/Forms/management/FormManagementAnalytics'
import { FormManagementCompletionPage } from '@/components/Forms/management/FormManagementCompletionPage'
import { FormManagementCrossAnalysis } from '@/components/Forms/management/FormManagementCrossAnalysis'
import { FormManagementCustomerWorkflow } from '@/components/Forms/management/FormManagementCustomerWorkflow'
import { FormManagementExport } from '@/components/Forms/management/FormManagementExport'
import { FormManagementLinkGenerator } from '@/components/Forms/management/FormManagementLinkGenerator'
import { FormManagementNav } from '@/components/Forms/management/FormManagementNav'
import { FormManagementOperations } from '@/components/Forms/management/FormManagementOperations'
import { FormManagementVisitorActivity } from '@/components/Forms/management/FormManagementVisitorActivity'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { FormManagementTabId } from '@/contracts/form-management'
import { formManagementTabLabels } from '@/contracts/form-management'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import {
  getFormManagementWorkspaceContract,
  getFormManagementWorkspaceData,
} from '@/lib/mock-api/form-management'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import { formManagementPaneDefaults, getTwoPaneGridTemplate } from '@/lib/resizable-panes'
import {
  formManagementSystemTabOrder,
  formManagementTabFeatureKeys,
  useSystemFeatureVisibility,
} from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { Lock } from 'lucide-react'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'

const statusMap = {
  draft: { label: '草稿', variant: 'secondary' },
  published: { label: '上線', variant: 'success' },
  closed: { label: '已關閉', variant: 'destructive' },
  archived: { label: '已封存', variant: 'secondary' },
} as const

type FormManagementDialogProps = {
  form: FormUiContract | null
  canManageForms: boolean
  folders: FormFolderContract[]
  open: boolean
  activeTab?: FormManagementTabId
  onOpenChange: (open: boolean) => void
  onTabChange?: (tab: FormManagementTabId) => void
  onEdit: (formId: string) => void
  onPreview: (formId: string) => void
  onArchiveToggle: (status: 'archived' | 'draft') => void
}

export function FormManagementDialog({
  form,
  canManageForms,
  folders,
  open,
  activeTab,
  onOpenChange,
  onTabChange,
  onArchiveToggle,
}: FormManagementDialogProps) {
  const [localTab, setLocalTab] = useState<FormManagementTabId>('completionPage')
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 280,
    config: formManagementPaneDefaults.nav,
  })
  const currentTab = activeTab ?? localTab
  const featureVisibility = useSystemFeatureVisibility()

  const workspaceContract = useMemo(
    () =>
      form
        ? getFormManagementWorkspaceContract({
            form,
            folders,
            canManageForms,
          })
        : null,
    [canManageForms, folders, form],
  )
  const workspaceData = useMemo(() => (form ? getFormManagementWorkspaceData(form) : null), [form])

  useEffect(() => {
    if (open) setLocalTab(activeTab ?? workspaceContract?.defaultTab ?? 'completionPage')
  }, [activeTab, open, workspaceContract?.defaultTab])

  if (!form || !workspaceContract || !workspaceData) return null
  const activeForm = form
  const activeWorkspaceData = workspaceData

  const status = statusMap[form.status]
  const publicUrl = buildPublicFormUrl(form.id)
  const managementDisabledReason = '只讀檢視：管理操作需要管理員權限。'
  const visibleTabs: FormManagementTabId[] = formManagementSystemTabOrder.filter((tab) => {
    if (tab === 'customerWorkflow' && activeForm.customerWorkflow !== 'customer_onboarding') {
      return false
    }
    const featureKey = formManagementTabFeatureKeys[tab]
    return featureKey ? featureVisibility[featureKey] : false
  })
  const resolvedTab = visibleTabs.includes(currentTab) ? currentTab : (visibleTabs[0] ?? null)
  const managementGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: formManagementPaneDefaults.nav,
  })

  function changeTab(tab: FormManagementTabId) {
    setLocalTab(tab)
    onTabChange?.(tab)
  }

  function renderPanel() {
    if (!resolvedTab) {
      return (
        <div className="grid min-h-[420px] place-items-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
          <div className="max-w-md">
            <Badge variant="secondary">System visibility</Badge>
            <h3 className="mt-4 text-lg font-semibold text-slate-950">問卷管理功能已隱藏</h3>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              目前此問卷的管理工作區頁籤都已被系統功能顯示設定隱藏。請由超級管理員到系統設定重新啟用需要的功能。
            </p>
          </div>
        </div>
      )
    }

    if (resolvedTab === 'analytics') {
      return <FormManagementAnalytics workspaceData={activeWorkspaceData} />
    }

    if (resolvedTab === 'export') {
      return (
        <FormManagementExport workspaceData={activeWorkspaceData} canManageForms={canManageForms} />
      )
    }

    if (resolvedTab === 'crossAnalysis') {
      return <FormManagementCrossAnalysis workspaceData={activeWorkspaceData} />
    }

    if (resolvedTab === 'visitorActivity') {
      return <FormManagementVisitorActivity workspaceData={activeWorkspaceData} />
    }

    if (resolvedTab === 'linkGenerator') {
      return <FormManagementLinkGenerator workspaceData={activeWorkspaceData} />
    }

    if (resolvedTab === 'completionPage') {
      return <FormManagementCompletionPage workspaceData={activeWorkspaceData} />
    }

    if (resolvedTab === 'customerWorkflow') {
      return (
        <FormManagementCustomerWorkflow
          workspaceData={activeWorkspaceData}
          canManageForms={canManageForms}
        />
      )
    }

    if (resolvedTab === 'operations') {
      return (
        <FormManagementOperations
          form={activeForm}
          folders={folders}
          workspaceData={activeWorkspaceData}
          canManageForms={canManageForms}
          onArchiveToggle={onArchiveToggle}
        />
      )
    }

    return (
      <div className="grid min-h-[420px] place-items-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
        <div className="max-w-md">
          <Badge variant="secondary">Mock-safe workspace shell</Badge>
          <h3 className="mt-4 text-lg font-semibold text-slate-950">
            {formManagementTabLabels[resolvedTab]}
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            此頁籤會在後續批次補上完整工作區內容。目前先建立 SurveyCake 式管理 Dialog 架構、tab
            狀態與權限邊界，不呼叫真實 provider。
          </p>
          {!canManageForms && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600">
              <Lock className="size-4" />
              只讀檢視，管理操作需要管理員權限。
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0">
        <div
          data-testid="form-management-grid"
          className="grid h-full min-h-0 w-full min-w-0 max-w-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-slate-50 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--form-management-grid-columns)] lg:grid-rows-1"
          style={{ '--form-management-grid-columns': managementGridColumns } as CSSProperties}
        >
          <div className="relative min-h-0 min-w-0 overflow-hidden">
            <div
              className={cn(
                'h-full transition-opacity duration-150',
                navCollapsed && 'pointer-events-none opacity-0',
              )}
              aria-hidden={navCollapsed}
            >
              <FormManagementNav
                activeTab={resolvedTab}
                tabs={visibleTabs}
                onTabChange={changeTab}
              />
            </div>
            <PaneResizeHandle
              edge="right"
              collapsed={navCollapsed}
              resizeLabel="拖曳調整問卷管理左欄寬度"
              collapseLabel="收合問卷管理左欄"
              expandLabel="展開問卷管理左欄"
              onResizeStart={startNavResize}
              onToggle={toggleNavCollapsed}
            />
          </div>
          <section className="flex min-h-0 min-w-0 flex-col">
            <div className="min-w-0 shrink-0 border-b border-slate-200 bg-white p-4 pr-14">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <DialogHeader>
                  <div className="flex flex-wrap items-center gap-2">
                    <DialogTitle>{form.title}</DialogTitle>
                    <Badge variant={status.variant}>{status.label}</Badge>
                    <Badge variant="outline">Synthetic</Badge>
                  </div>
                  <DialogDescription className="sr-only">問卷管理工作區</DialogDescription>
                </DialogHeader>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="rounded-md bg-slate-100 px-2 py-1">{form.responseCount} 回應</span>
                <span className="rounded-md bg-slate-100 px-2 py-1">
                  完成率 {form.completionRate}%
                </span>
                <span className="min-w-0 break-all rounded-md bg-slate-100 px-2 py-1">
                  URL: {publicUrl}
                </span>
                {!canManageForms ? (
                  <span
                    id="form-management-readonly-note"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-slate-600"
                  >
                    <Lock className="size-3.5" />
                    {managementDisabledReason}
                  </span>
                ) : null}
              </div>
            </div>
            <ScrollArea key={resolvedTab} className="min-h-0 flex-1" viewportClassName="p-4 lg:p-5">
              <div
                id={`form-management-panel-${resolvedTab ?? 'hidden'}`}
                role="tabpanel"
                aria-labelledby={resolvedTab ? `form-management-tab-${resolvedTab}` : undefined}
                className={cn(
                  'mx-auto w-[calc(100dvw-52px)] max-w-[calc(100dvw-52px)] md:w-full md:max-w-7xl',
                )}
              >
                {renderPanel()}
              </div>
            </ScrollArea>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
